import React from 'react'
import { useTranslation } from 'react-i18next'

import { HStack } from '~/components/elements/HStack'
import {
  IconButton,
  type IconButtonProps,
} from '~/components/elements/IconButton'
import { Typography } from '~/components/elements/Typography'
import { VStack } from '~/components/elements/VStack'
import { ShareDropDownMenuButton } from '~/components/features/ShareDropDownMenuButton'

import { useFigureInfo } from '../hooks/useFigureInfo'
import { useRestoringParams } from '../hooks/useRestoringParams'

import { HowInput } from './HowInput'
import { SegmentRoutesClearButton } from './SegmentRoutesClearButton'
import styles from './StrategyInput.css'
import { StrategyPanel } from './StrategyPanel'
import { StrategyTypeInput } from './StrategyTypeInput'
import { StrategyTypeNote } from './StrategyTypeNote'
import { ToggleShowArrowButton } from './ToggleShowArrowButton'
import { WhatInput } from './WhatInput'
import { WhoInput } from './WhoInput'

export type StrategyInputProps = {
  index: number
  onDelete: (index: number) => void
}

export const StrategyInput = ({ index, onDelete }: StrategyInputProps) => {
  const { t } = useTranslation()
  const handleDeleteButtonClick = React.useCallback<
    EH<IconButtonProps, 'onClick'>
  >(() => {
    onDelete(index)
  }, [index, onDelete])

  const label = React.useMemo(
    () => t('customer_strategyindex_1', { index: index + 1 }),
    [index, t],
  )
  const [image, setImage] = React.useState<HTMLDivElement | null>(null)
  const restoringParams = useRestoringParams()
  const figureInfo = useFigureInfo()

  return (
    <VStack id={`memo-${index + 1}`} spacing={2}>
      <HStack align="center" spacing={0}>
        <IconButton
          buttonStyle="light"
          fillMode="flat"
          icon="trash"
          onClick={handleDeleteButtonClick}
        />

        <Typography variant="heading2">{label}</Typography>
      </HStack>

      <VStack spacing={2}>
        <VStack alignSelf="start" spacing={2}>
          <HStack align="center" justify="start" spacing={1}>
            <StrategyTypeInput index={index} />

            <SegmentRoutesClearButton index={index} />
          </HStack>

          <HStack align="center" justify="space-between" spacing={2}>
            <StrategyTypeNote index={index} />

            <HStack align="center" spacing={1}>
              <ToggleShowArrowButton index={index} />

              {image && (
                <ShareDropDownMenuButton
                  downloadAsPng={{ image, name: label }}
                  getSharedUrl={{ restoringParams }}
                  saveDocumentImage={{
                    figureInfo,
                    image,
                    initialComment: '',
                    restoringParams,
                  }}
                />
              )}
            </HStack>
          </HStack>

          <StrategyPanel ref={setImage} index={index} />
        </VStack>

        <HStack justify="space-between" spacing={2}>
          <WhoInput className={styles.who} index={index} />

          <WhatInput className={styles.what} index={index} />
        </HStack>

        <HowInput index={index} />
      </VStack>
    </VStack>
  )
}
